import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Job } from "@/lib/schema";
import { COMMON_CLASSES } from "@/lib/jobConstants";
import { JobDescription } from "./JobDescription";
import { JobSkills } from "./JobSkills";
import { RecruitmentStatusIndicator } from "./RecruitmentStatusIndicator";
import { formatDisplayText, formatDateForSouthAfrica } from "@/lib/textUtils";
import industryIcon from "@/assets/jobs/icon_industry.svg";
import locationIcon from "@/assets/jobs/icon_location.svg";
import calendarIcon from "@/assets/jobs/icon_calendar.svg";
import { AlertTriangle } from "lucide-react";

interface JobInfoCardProps {
  job: Job;
}

export function JobInfoCard({ job }: JobInfoCardProps) {

  return (
    <Card className={COMMON_CLASSES.card}>
      <CardContent className="p-4 sm:p-6">
        {/* Job Header */}
        <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
          {/* Company logo area removed for privacy */}
          <div className="flex-1 w-full">
            <h2 className={`${COMMON_CLASSES.text.heading} mb-2`}>{job.title}</h2>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-gray-600 mb-4">
              {/* Company name hidden for privacy - removed entire section */}
              <div className="flex items-center gap-1">
                <img src={locationIcon} alt="Location" className="h-4 w-4" />
                <span>{job.location}</span>
              </div>
              <div className="flex items-center gap-1">
                <img src={industryIcon} alt="Department" className="h-4 w-4" />
                <span>{formatDisplayText(job.category)}</span>
              </div>
              {job.salary && (
                <div className="flex items-center gap-1">
                  <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{job.salary}</span>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <Badge variant="outline" className="border-0 bg-gray-100 text-gray-700 w-fit">
                {formatDisplayText(job.employmentType)}
              </Badge>
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <img src={calendarIcon} alt="Calendar" className="h-3 w-3" />
                <span>
                  Posted {formatDateForSouthAfrica(job.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Recruitment Status with Callout */}
        <RecruitmentStatusIndicator 
          status={job.recruitmentStatus} 
          size="lg" 
          showCallout 
        />

        {/* Job Description */}
        <JobDescription 
          description={job.description}
          containerClassName="space-y-4 pb-6"
        />

        {/* Important Notes Callout */}
        {job.importantNotes && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-amber-800 space-grotesk mb-1">
                Important Notes
              </h4>
              <p className="text-sm text-slate-700 whitespace-pre-line font-manrope">
                {job.importantNotes}
              </p>
            </div>
          </div>
        )}

        {/* Skills */}
        <JobSkills skills={job.skills} />
      </CardContent>
    </Card>
  );
}
